import { TONE_BG, TONE_TEXT, TONE_WASH, connectorTone, statusTone } from "../../lib/status";
import type { StatusTone } from "../../lib/status";

/**
 * Status is shown as a small square plus the literal protocol string.
 *
 * A square rather than a dot, because the rest of the interface has no curves;
 * and *plus the string* rather than instead of it, because colour alone is not a
 * channel — a red/green distinction is invisible to a meaningful share of
 * operators, and the exact OCPP state is information the collapsed tone throws
 * away.
 */

export function StatusMark({ tone }: { readonly tone: StatusTone }) {
  return <span aria-hidden="true" className={`block size-2 shrink-0 ${TONE_BG[tone]}`} />;
}

export function ConnectorStatus({
  status,
  errorCode,
}: {
  readonly status: string;
  readonly errorCode: string;
}) {
  const tone = connectorTone(status, errorCode);
  const hasFault = errorCode !== "" && errorCode !== "NoError";

  return (
    <span className="inline-flex items-center gap-2">
      <StatusMark tone={tone} />
      <span className={`text-data ${TONE_TEXT[tone]}`}>{status}</span>
      {/* The error code is only rendered when there is one, so its presence in a
          row is itself the signal. */}
      {hasFault ? (
        <span className="text-micro tracking-normal normal-case text-signal">{errorCode}</span>
      ) : null}
    </span>
  );
}

/**
 * Connection is a different axis from status and must not be merged with it. A
 * charge point can hold a recent heartbeat and no live socket — spec 10.3's
 * registry is the only thing that knows — and that combination is precisely what
 * an operator is hunting for. Two marks, never one.
 */
export function ConnectionStatus({
  connected,
  stale,
}: {
  readonly connected: boolean;
  readonly stale: boolean;
}) {
  if (connected) {
    return (
      <span className="inline-flex items-center gap-2">
        <StatusMark tone="available" />
        <span className="text-data text-state-available">Online</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <StatusMark tone={stale ? "faulted" : "idle"} />
      <span className={`text-data ${stale ? "text-signal" : "text-ink-faint"}`}>
        {stale ? "Offline" : "Disconnected"}
      </span>
    </span>
  );
}

export function Pill({
  tone = "idle",
  children,
}: {
  readonly tone?: StatusTone;
  readonly children: string;
}) {
  return (
    <span
      className={`inline-block rounded-hairline px-1.5 py-0.5 text-micro font-semibold uppercase ${TONE_WASH[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * A state in a table row, weighted by how much it asks of the operator.
 *
 * A row in a state worth looking at — live, transitional, faulted — gets the
 * filled pill. A quiet ending (`available`, `idle`: a normal close, a completed
 * order) is set as a faint word instead. Those are most rows and the least
 * interesting, and drawing them as pills too is what made a column of states read
 * as one flat stripe: the pills that remain are the rows to open.
 */
export function RowState({ tone, children }: { readonly tone: StatusTone; readonly children: string }) {
  if (tone === "available" || tone === "idle") {
    return <span className="text-data text-ink-faint">{children}</span>;
  }
  return <Pill tone={tone}>{children}</Pill>;
}

/**
 * Registration status (spec 2.1). `Pending` and `Rejected` are transitional and
 * fault respectively — a rejected station is not talking to us and someone needs
 * to know.
 */
export function RegistrationPill({ status }: { readonly status: string }) {
  const tone: StatusTone =
    status === "Accepted"
      ? "available"
      : status === "Pending"
        ? "transitional"
        : status === "Rejected"
          ? "faulted"
          : "idle";

  return <Pill tone={tone}>{status}</Pill>;
}

export { statusTone };
